import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, NavLink, useParams } from "react-router-dom";
import api from "@/lib/api";
import { BannerEditor } from "@/components/banner/BannerEditor";
import { GalleryHorizontal, Loader2, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { downloadBlob } from "@/lib/workflow";

function BannerList({ list }) {
  return (
    <nav className="space-y-1.5" data-testid="banner-list">
      {list.map((b) => (
        <NavLink key={b.id} to={`/partner/werbebanner/${b.id}`} data-testid={`banner-list-item-${b.id}`}
          className={({ isActive }) => `block rounded-lg border px-3 py-2.5 transition-colors ${isActive ? "border-[#005448] bg-[#005448] text-white" : "border-slate-200 bg-white hover:border-slate-300"}`}>
          {({ isActive }) => (<>
            <span className="block text-sm font-semibold truncate">{b.name}{b.location && <span className={isActive ? "text-white/70 font-normal" : "text-slate-400 font-normal"}> · {b.location}</span>}</span>
            <span className={`flex items-center gap-2 text-xs ${isActive ? "text-white/80" : "text-slate-500"}`}>
              <span className="h-1.5 flex-1 rounded-full bg-black/10 overflow-hidden"><span className={`block h-full ${isActive ? "bg-white" : "bg-[#005448]"}`} style={{ width: `${b.slots_total ? (b.slots_taken / b.slots_total) * 100 : 0}%` }} /></span>
              {b.slots_total ? `${b.slots_taken}/${b.slots_total}` : "neu"}
            </span>
          </>)}
        </NavLink>
      ))}
    </nav>
  );
}

export default function BannerPlanner() {
  const { spaceId } = useParams();
  const [list, setList] = useState(null);
  const load = useCallback(() => { api.get("/banner").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  useEffect(() => { load(); }, [load]);
  if (!list) return <div className="p-10 grid place-items-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (!spaceId && list.length) return <Navigate to={`/partner/werbebanner/${list[0].id}`} replace />;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="banner-planner">
      <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-3"><GalleryHorizontal className="text-[#005448]" /> Werbebanner</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">Visuelle Planung der Sammelbanner: Logofelder per Maus verschieben und am Eckpunkt proportional vergrößern (Logos bleiben auf der Mittellinie), Sperrbereiche setzen. Die Zuordnung erscheint automatisch im Profil des Partners.</p>
      </div>
      {list.length > 0 && <Button variant="outline" onClick={() => downloadBlob("/banner/export/pdf", {}, `Werbebanner ${new Date().toISOString().slice(0, 10)}.pdf`)} data-testid="banner-pdf-all"><FileDown size={15} className="mr-1.5" /> Alle Banner als PDF</Button>}
      </div>
      {list.length === 0
        ? <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-sm text-slate-600" data-testid="banner-empty">Noch keine Sammelbanner. Im <Link to="/partner/leistungskatalog" className="text-blue-600 hover:underline">Leistungskatalog</Link> bei einer Fläche den Schalter „Sammelbanner“ einschalten.</div>
        : <div className="grid gap-6 grid-cols-[minmax(0,1fr)] lg:grid-cols-[240px_minmax(0,1fr)]">
            <BannerList list={list} />
            {spaceId && <BannerEditor key={spaceId} spaceId={spaceId} onSaved={load} />}
          </div>}
    </div>
  );
}
